import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Settings } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface PricingTier {
  id: string;
  tier_name: string;
  max_jobs_per_month: number | null;
}

interface Organization {
  id: string;
  name: string;
  slug: string | null;
  plan: string | null;
  pricing_tier_id: string | null;
  subscription_status: string | null;
  elevenlabs_agent_id: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

interface OrgDetailsEditorProps {
  org: Organization;
  onUpdate: () => void;
}

export function OrgDetailsEditor({ org, onUpdate }: OrgDetailsEditorProps) {
  const [open, setOpen] = useState(false);
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [formData, setFormData] = useState({
    name: org.name,
    slug: org.slug || "",
    pricing_tier_id: org.pricing_tier_id || "",
    subscription_status: org.subscription_status || "inactive",
    elevenlabs_agent_id: org.elevenlabs_agent_id || "",
    email: org.email || "",
    phone: org.phone || "",
    address: org.address || "",
    city: org.city || "",
    state: org.state || "",
    zip: org.zip || ""
  });

  useEffect(() => {
    if (open) {
      loadPricingTiers();
    }
  }, [open]);

  const loadPricingTiers = async () => {
    try {
      const { data, error } = await supabase
        .from('pricing_tiers')
        .select('*')
        .order('tier_name');

      if (error) throw error;
      setPricingTiers(data || []);
    } catch (error: any) {
      console.error('Failed to load pricing tiers:', error);
      toast.error("Failed to load pricing tiers");
    }
  };

  const handleSave = async () => {
    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          name: formData.name,
          slug: formData.slug || null,
          pricing_tier_id: formData.pricing_tier_id || null,
          subscription_status: formData.subscription_status,
          elevenlabs_agent_id: formData.elevenlabs_agent_id || null,
          email: formData.email || null,
          phone: formData.phone || null,
          address: formData.address || null,
          city: formData.city || null,
          state: formData.state || null,
          zip: formData.zip || null
        })
        .eq('id', org.id);

      if (error) throw error;

      toast.success("Organization updated successfully");
      setOpen(false);
      onUpdate();
    } catch (error: any) {
      console.error('Failed to update organization:', error);
      toast.error("Failed to update organization");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Settings className="w-4 h-4" />
          Edit Details
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Organization Details</DialogTitle>
          <DialogDescription>
            Update organization information and settings
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Organization Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="my-company"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pricing_tier">Pricing Tier</Label>
              <Select 
                value={formData.pricing_tier_id} 
                onValueChange={(value) => setFormData({ ...formData, pricing_tier_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a tier" />
                </SelectTrigger>
                <SelectContent>
                  {pricingTiers.map((tier) => (
                    <SelectItem key={tier.id} value={tier.id}>
                      {tier.tier_name}
                      {tier.max_jobs_per_month && ` (${tier.max_jobs_per_month} jobs/month)`}
                      {!tier.max_jobs_per_month && ' (Unlimited)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="subscription_status">Subscription Status</Label>
              <Select value={formData.subscription_status} onValueChange={(value) => setFormData({ ...formData, subscription_status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="elevenlabs_agent_id">ElevenLabs Agent ID</Label>
            <Input
              id="elevenlabs_agent_id"
              value={formData.elevenlabs_agent_id}
              onChange={(e) => setFormData({ ...formData, elevenlabs_agent_id: e.target.value })}
              placeholder="agent_xxxxx"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="CA"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="zip">ZIP</Label>
              <Input
                id="zip"
                value={formData.zip}
                onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
